<!-- eslint-disable camelcase -->
<template>
  <div>
    <div
      v-editable="blok"
      class="sign-up"
      :class="showThankYou ? 'sign-up__form-submitted' : ''"
    >
      <CustomRTE :blok="blok.heading[0]" />
      <div class="sign-up__success">
        <h4>Thank you!</h4>
        <p>The form was successfully submitted.</p>
      </div>
      <form novalidate class="mr-auto ml-0" @submit.prevent="submit">
        <ul class="sign-up__list">
          <li
            class="sign-up__field"
            :class="errors.first_name ? 'sign-up__field--error' : ''"
          >
            <label for="first_name">First Name</label>
            <input
              id="first_name"
              v-model="first_name"
              type="text"
              name="first_name"
              required
              @input="handleInput"
            />
            <p class="sign-up__field-error">{{ errors.first_name }}</p>
          </li>
          <li
            class="sign-up__field"
            :class="errors.last_name ? 'sign-up__field--error' : ''"
          >
            <label for="last_name">Last Name</label>
            <input
              id="last_name"
              v-model="last_name"
              type="text"
              name="last_name"
              required
              @input="handleInput"
            />
            <p class="sign-up__field-error">{{ errors.last_name }}</p>
          </li>
          <li
            class="sign-up__field"
            :class="errors.email ? 'sign-up__field--error' : ''"
          >
            <label for="email">Email</label>
            <input
              id="email"
              v-model="email"
              type="email"
              name="email"
              required
              @input="handleInput"
            />
            <p class="sign-up__field-error">{{ errors.email }}</p>
          </li>
          <li
            class="sign-up__field"
            :class="errors.password ? 'sign-up__field--error' : ''"
          >
            <label for="password">Password</label>
            <div class="sign-up__password-container">
              <div class="sign-up__icon" @click="togglePassword()">
                <div
                  v-if="showPassword"
                  class="material-symbols-outlined sign-up__icon-click"
                >
                  visibility
                </div>
                <div
                  v-else
                  class="material-symbols-outlined sign-up__icon-click"
                >
                  visibility_off
                </div>
              </div>
              <input
                v-if="showPassword"
                id="password"
                v-model="password"
                type="text"
                name="password"
                required
                @input="handleInput"
              />
              <input
                v-else
                id="password"
                v-model="password"
                type="password"
                name="password"
                required
                @input="handleInput"
              />
            </div>

            <p class="sign-up__field-error">{{ errors.password }}</p>
          </li>
          <li
            class="sign-up__field"
            :class="errors.password ? 'sign-up__field--error' : ''"
          >
            <label for="password_confirmation">Verify Password</label>
            <div class="sign-up__password-container">
              <div class="sign-up__icon" @click="toggleConfirmPassword()">
                <div
                  v-if="showConfirmPassword"
                  class="material-symbols-outlined sign-up__icon-click"
                >
                  visibility
                </div>
                <div
                  v-else
                  class="material-symbols-outlined sign-up__icon-click"
                >
                  visibility_off
                </div>
              </div>

              <input
                v-if="showConfirmPassword"
                id="password_confirmation"
                v-model="password_confirmation"
                type="text"
                name="password_confirmation"
                required
                @input="handleInput"
              />
              <input
                v-else
                id="password_confirmation"
                v-model="password_confirmation"
                type="password"
                name="password_confirmation"
                required
                @input="handleInput"
              />
            </div>
            <p class="sign-up__field-error">
              {{ errors.password_confirmation }}
            </p>
          </li>
          <li
            class="sign-up__field"
            :class="errors.license_status ? 'sign-up__field--error' : ''"
          >
            <label for="license_status">License Status</label>
            <select
              id="license_status"
              v-model="license_status"
              name="license_status"
              required
              @input="handleInput"
            >
              <option value="">Select your status</option>
              <option value="licensed">
                I am currently a Licensed Healthcare Professional
              </option>
              <option value="not_licensed">
                I am not a Licensed Healthcare Professional
              </option>
            </select>
            <p class="sign-up__field-error">{{ errors.license_status }}</p>
          </li>
        </ul>
        <div class="sign-up__tos">
          <input
            id="tos"
            v-model="tos"
            type="checkbox"
            name="tos"
            @input="handleInput"
          />
          <label
            v-if="richtextLegalModified"
            v-editable="blok"
            for="tos"
            v-html="richtextLegalModified"
          />
          <p class="sign-up__field-error">{{ errors.tos }}</p>
        </div>
        <button type="submit" :disabled="isClicked" class="sign-up__submit">
          Sign up
        </button>
        <div
          v-if="richtextLogin"
          v-editable="blok"
          class="sign-up__login"
          v-html="richtextLogin"
        />
      </form>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { ISbSchema } from '@storyblok/js';
import { SignUpBlokProps } from '@/types';
// Plugins
const { $rteSchema } = useNuxtApp();
// Props
const props = defineProps<{
  blok: SignUpBlokProps;
}>();
const isClicked = ref(false);
// Legal Text
const richtextLegal = computed(() => {
  return props.blok.legal_text &&
    props.blok.legal_text.content &&
    props.blok.legal_text.content[0].content
    ? renderRichText(props.blok.legal_text, {
        schema: $rteSchema as ISbSchema | undefined,
      })
    : '';
});
// Modified Legal Text (changing p to span)
const richtextLegalModified = computed(() => {
  return richtextLegal.value
    .replace('<p>', '<span>')
    .replace('</p>', '</span>');
});
// Login Text
const richtextLogin = computed(() => {
  return props.blok.login_text &&
    props.blok.login_text.content &&
    props.blok.login_text.content[0].content
    ? renderRichText(props.blok.login_text, {
        schema: $rteSchema as ISbSchema | undefined,
      })
    : '';
});
const specialty = ref('');
const licencedTitle = ref('Are you a licensed professional?');
const school = ref('No');
const license_status = ref('');
// eslint-disable-next-line camelcase
const first_name = ref('');
// eslint-disable-next-line camelcase
const last_name = ref('');
const email = ref('');
const password = ref('');
// eslint-disable-next-line camelcase
const password_confirmation = ref('');
const tos = ref(false);
const showThankYou = ref(false);
const errors = ref<Record<string, any>>({});
type FieldMapEntry = {
  ref: globalThis.Ref;
  label: string;
};
const fieldMap: Record<string, FieldMapEntry> = {
  email: { label: 'Email', ref: email },
  // eslint-disable-next-line camelcase
  first_name: { label: 'First name', ref: first_name },
  // eslint-disable-next-line camelcase
  last_name: { label: 'Last name', ref: last_name },
  password: { label: 'Password', ref: password },
  // eslint-disable-next-line camelcase
  password_confirmation: {
    label: 'Verify Password',
    // eslint-disable-next-line camelcase
    ref: password_confirmation,
  },
  license_status: { label: 'License Status', ref: license_status }, // <-- add this line
};
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const togglePassword = () => {
  showPassword.value = !showPassword.value;
};
const toggleConfirmPassword = () => {
  showConfirmPassword.value = !showConfirmPassword.value;
};

const validateRequired = () => {
  const keys = Object.keys(fieldMap);
  keys.forEach((fieldname) => {
    const value = fieldMap[fieldname].ref.value.trim();
    if (value.length < 1) {
      errors.value[fieldname] = fieldMap[fieldname].label + ' is required.';
    } else {
      delete errors.value[fieldname];
    }
  });
};
const validateEmail = () => {
  if (errors.value.email) {
    return;
  }
  if (!/^\S+@\S+\.\S+$/.test(email.value)) {
    errors.value.email = 'Please provide a valid email address.';
  } else {
    delete errors.value.email;
  }
};
const validatePassword = () => {
  if (errors.value.password) {
    return;
  }

  const errorMessage = ref('');
  if (password.value.length < 14) {
    errorMessage.value += 'Password should be at least 14 characters long. ';
  }
  if (!/[\p{Z}\p{S}\p{P}]+/gu.test(password.value)) {
    errorMessage.value += 'At least one special character. ';
  }
  if (password.value !== password_confirmation.value) {
    errorMessage.value += 'Passwords do not match.';
  }

  if (errorMessage.value !== '') {
    errors.value.password = errorMessage;
  } else {
    delete errors.value.password;
  }
};
const validateTos = () => {
  if (errors.value.tos) {
    return;
  }
  if (!tos.value) {
    errors.value.tos = 'Please accept and check this box to proceed.';
  } else {
    delete errors.value.tos;
  }
};
const handleInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const name = target.name;
  if (name === 'tos') {
    tos.value = target.checked;
  }
  if (Object.keys(errors.value).length) {
    if (errors.value.first_name) {
      // eslint-disable-next-line camelcase
      if (first_name.value.trim() !== '') {
        delete errors.value.first_name;
      }
    }
    if (errors.value.last_name) {
      // eslint-disable-next-line camelcase
      if (last_name.value.trim() !== '') {
        delete errors.value.last_name;
      }
    }
    if (errors.value.email) {
      if (/^\S+@\S+\.\S+$/.test(email.value)) {
        delete errors.value.email;
      }
    }
    if (errors.value.password) {
      if (password.value.length >= 8) {
        delete errors.value.password;
        // eslint-disable-next-line camelcase
      }
    }
    if (errors.value.tos) {
      if (tos.value) {
        delete errors.value.tos;
      }
    }
  }
};
const submit = () => {
  isClicked.value = true;
  validateRequired();
  validateEmail();
  validatePassword();
  validateTos();
  const endpoint = computed(() => {
    return props.blok.form_endpoint
      ? props.blok.form_endpoint
      : useRuntimeConfig()?.public?.signUpURL;
  });
  if (Object.keys(errors.value).length === 0) {
    if (license_status.value === 'not_licensed') {
      window.location.href = 'https://www.shiftkey.com/join/unlicensed';
      return;
    }
    fetch(endpoint.value, {
      body: JSON.stringify({
        email: email.value,
        first_name: first_name.value,
        last_name: last_name.value,
        password: password.value,
        password_confirmation: password_confirmation.value,
        tos: tos.value,
      }),
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
      method: 'POST',
      redirect: 'follow',
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 'OK') {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            email: email.value,
            event: 'user_sign_up',
          });
        }
        return data;
      })
      .then((data) => {
        if (data.status === 'OK') {
          analytics.track('User Sign Up', {
            email: email.value,
            form_id: 'Get-Started-Form',
          });
          //window.optimizely = window.optimizely || [];
          //window.optimizely.push({
           // type: 'event',
           // eventName: 'optiUserSignUp',
           // tags: {
           //   revenue: 0, // Optional in cents as integer (500 == $5.00)
           //   value: 0.0, // Optional as float
           // },
           //});
          window.location.href = data.redirect;
          email.value = '';
          first_name.value = '';
          last_name.value = '';
          password.value = '';
          password_confirmation.value = '';
          tos.value = false;
          isClicked.value = false;
        } else {
          const serverErrors = data?.errors ?? {};
          Object.keys(serverErrors).forEach((key) => {
            errors.value[key] = serverErrors[key].join(', ');
          });
          isClicked.value = false;
        }
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  } else {
    isClicked.value = false;
  }
};
watch(specialty, (newVal) => {
  if (newVal === 'nl') {
    licencedTitle.value = 'Are you still in school?';
    school.value = 'Yes';
  } else if (newVal !== '') {
    licencedTitle.value = 'Do you have an active ' + newVal + ' license?';
    school.value = 'No';
  } else {
    licencedTitle.value = 'Are you a licensed professional?';
  }
});
</script>
<style lang="scss">
.sign-up {
  @include container;
  .custom-rte {
    max-width: 540px;
  }
  form {
    margin-block-start: 40px;
    max-width: 844px;
  }
  input {
    outline: none;
    &:focus-visible {
      outline: 1px solid var(--lightest);
      outline-offset: 2px;
    }
  }
  a {
    color: var(--lightest);
    &:focus-visible {
      outline: 1px solid var(--lightest);
      outline-offset: 2px;
    }
  }
  &__icon {
    position: absolute;
    right: 18px;
    top: 55%;
    transform: translateY(-50%);
    cursor: pointer;
    z-index: 2;
  }
  &__icon-click {
    cursor: pointer;

    &:hover {
      color: var(--lightest) !important;
    }
  }
  &__password-container {
    position: relative;
  }
  &__list {
    display: grid;
    gap: var(--spacing-xs);
    grid-template-columns: 1fr 1fr;
    margin-block: 0;
    margin-inline: 0;
    padding-block: 0;
    padding-inline: 0;
  }
  &__field {
    display: flex;
    flex-direction: column;
    grid-column: span 2;
    position: relative;
    &::before {
      content: none;
    }
    &:nth-of-type(1),
    &:nth-of-type(2) {
      @media all and #{$mq-m-min} {
        grid-column: span 1;
      }
    }
    /* stylelint-disable-next-line no-descending-specificity */
    input,
    select {
      background-color: transparent;
      border: 1px solid var(--neutral);
      border-radius: 2px;
      min-height: 51px;
      padding-block: 12px;
      padding-inline: 24px;
      width: 100%;
      &:focus {
        outline: none;
      }
      &:focus-visible {
        border-width: 2px;
      }
    }
    label {
      color: var(--neutral);
      font-size: 0.875rem;
      margin-block-end: 8px;
      &::after {
        content: '*';
      }
    }
    &--error {
      /* stylelint-disable-next-line no-descending-specificity */
      input {
        border-color: var(--red);
      }
    }
  }
  &__field-error {
    color: var(--red);
    font-size: 0.75rem;
    inset-block-start: 100%;
    inset-inline-start: 0;
    margin-block-start: 4px;
  }
  &__tos {
    align-items: flex-start;
    display: flex;
    gap: 14px;
    margin: var(--spacing-m) 0;
    position: relative;
    span {
      --min-font: 14;
      --max-font: 18;
      display: inline-block;
      line-height: 150%;
      padding-block-start: 2px;
    }
    /* stylelint-disable-next-line no-descending-specificity */
    input,
    select {
      appearance: none;
      background-color: transparent;
      /* stylelint-disable-next-line value-keyword-case */
      border: 1px solid var(--neutral);
      border-radius: 2px;
      /* stylelint-disable-next-line value-keyword-case */
      color: currentColor;
      cursor: pointer;
      display: grid;
      font: inherit;
      height: 24px;
      margin: 3px 0 0;
      place-content: center;
      transform: translateY(-0.075em);
      width: 24px;
      &::before {
        /* stylelint-disable-next-line value-keyword-case */
        background-color: var(--lightest);
        box-shadow: inset 16px 16px var(--darkest);
        clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
        content: '';
        height: 10px;
        transform: scale(0);
        transform-origin: bottom left;
        width: 10px;
      }
      &:focus-visible {
        /* stylelint-disable-next-line value-keyword-case */
        outline: max(2px, 0.15em) solid currentColor;
        outline-offset: max(2px, 0.15em);
      }
      &:disabled {
        cursor: not-allowed;
      }
      &:checked {
        /* stylelint-disable-next-line value-keyword-case */
        background-color: currentColor;
        /* stylelint-disable-next-line value-keyword-case */
        border-color: currentColor;
        &::before {
          transform: scale(1);
        }
      }
    }
  }
  &__submit {
    @include primary-button;
  }
  &__login {
    color: var(--neutral);
    margin-top: var(--spacing-xs);
    p {
      font-size: 0.875rem;
    }
  }
  &__success {
    display: none;
    margin-block-start: var(--spacing-xl);
    h4 {
      margin-block-end: var(--spacing-xs);
    }
  }
  &__form-submitted {
    form {
      display: none;
    }
    .sign-up__success {
      display: block;
    }
  }
}
/* Modal test Styles */
/* Modal test Styles */
.modal-backdrop {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
.modal {
  background: var(--chalk);
  overflow-x: auto;
  display: flex;
  flex-direction: column;
  border-radius: 2px var(--section-border-radius) 2px 2px;
  background-image: url('/img/textures/texture-3.png');
  background-position: top center;
  background-repeat: repeat;
  margin-inline: 4px;
}
.modal-header,
.modal-footer {
  display: flex;
}
.modal-header {
  position: relative;
  justify-content: space-between;
}
.modal-footer {
  flex-direction: column;
}
.modal-body {
  position: relative;
  padding: 20px 10px;
}
.btn-close {
  cursor: pointer;
}
.btn-close:disabled,
button[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn-green {
  color: white;
  background: #4aae9b;
  border: 1px solid #4aae9b;
  border-radius: 2px;
}
.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.5s ease;
}
.hide-modal {
  display: none;
}
.lead-select {
  width: 100%;
  height: 51px;
  overflow: hidden;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
  padding-left: 5px;
  padding-right: 5px;
  margin-block-start: 0.875rem !important;
}
.lead-select:focus {
  outline: none !important;
  &:focus-visible {
    outline: 1px solid var(--lightest);
    outline-offset: 2px;
  }
}
.hide-experiment {
  display: none;
}
</style>
